import { setMode, setTheme } from '../../runtime/theme.js';
import { readPrefsState, writePrefs } from '../../runtime/prefs.js';
import { LitElement, html, css } from 'lit';
import { classMap } from 'lit/directives/class-map.js';

const THEME_EVENT = 'crusher:themechange';
const BRAND_EVENT = 'crusher:brandchange';

// Sanitization allowlist for the `default-brand` attribute. Rejects
// OS-coupled (`system-*`), context-coupled (`currentColor`, `var()`) and
// unsupported function-call forms (`color-mix`, `lab`, `lch`). The final
// CSS.supports() gate filters arbitrary garbage without bundling the
// CSS named-color list.
function isAllowedDefaultBrand(value) {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (lower === 'currentcolor' || lower === 'transparent') return false;
  if (lower.startsWith('system-')) return false;
  if (lower.startsWith('var(') || lower.startsWith('color-mix(')) return false;
  if (lower.startsWith('lab(') || lower.startsWith('lch(')) return false;
  return typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
    ? CSS.supports('color', trimmed)
    : true;
}

export class CrusherStyleSwitcher extends LitElement {
  static styles = css`
    .style-switcher {
      box-sizing: border-box;
      position: fixed;
      right: 0;
      top: var(--crusher-style-switcher-top, calc(var(--crusher-spacing-10) + var(--crusher-spacing-4) + var(--crusher-spacing-1)));
      padding: calc(var(--crusher-spacing-4) - var(--crusher-component-border-weight));
      width: var(--crusher-style-switcher-width, calc(var(--crusher-spacing-10) * 5));
      border-radius: calc(var(--crusher-spacing-1) + var(--crusher-component-border-weight));
      z-index: calc(var(--crusher-z-dropdown) * 2);
      transition: transform var(--crusher-motion-duration-slow) var(--crusher-motion-easing-inout);
      transform: translateX(100%);
      background: var(--crusher-style-switcher-bg, var(--bg-black-100, var(--crusher-background-surface)));
      border: var(--crusher-style-switcher-border-width, var(--crusher-component-border-weight)) solid var(--crusher-style-switcher-border, var(--bg-black-50, var(--crusher-border-primary)));
      box-shadow: var(--crusher-style-switcher-shadow, var(--outer-shadow, none));
    }
    .style-switcher.open {
      transform: translateX(calc(-1 * var(--crusher-style-switcher-open-offset, calc(var(--crusher-spacing-6) + var(--crusher-component-border-weight)))));
    }
    /* ---- Inline mode ----------------------------------------------------
       Render as an in-flow block: no fixed positioning, no slide-out
       transform, no gear toggle, panel always visible. Consumers can still
       customise width via --crusher-style-switcher-width. */
    :host([inline]) { display: block; }
    :host([inline]) .style-switcher {
      position: static;
      top: auto;
      right: auto;
      transform: none;
      width: 100%;
      max-width: var(--crusher-style-switcher-inline-max-width, none);
    }
    :host([inline]) .style-switcher-toggler,
    :host([inline]) .day-night {
      position: static;
      margin-right: 0;
      animation: none;
    }
    :host([inline]) .style-switcher-toggler { display: none; }
    .s-icon {
      appearance: none;
      position: absolute;
      height: var(--crusher-spacing-10);
      width: var(--crusher-spacing-10);
      text-align: center;
      font-size: var(--crusher-font-size-xl);
      color: var(--crusher-style-switcher-icon-color, var(--text-black-900, var(--crusher-text-primary)));
      right: 100%;
      border: var(--crusher-style-switcher-border-width, var(--crusher-component-border-weight)) solid var(--crusher-style-switcher-border, var(--bg-black-50, var(--crusher-border-primary)));
      margin-right: var(--crusher-style-switcher-open-offset, calc(var(--crusher-spacing-6) + var(--crusher-component-border-weight)));
      cursor: var(--crusher-style-switcher-cursor, pointer);
      transition:
        background-color var(--crusher-motion-duration-slow) var(--crusher-motion-easing-inout),
        box-shadow var(--crusher-motion-duration-slow) var(--crusher-motion-easing-inout),
        color var(--crusher-motion-duration-slow) var(--crusher-motion-easing-inout),
        transform var(--crusher-motion-duration-slow) var(--crusher-motion-easing-inout);
      border-radius: var(--crusher-radius-full);
      background: var(--crusher-style-switcher-bg, var(--bg-black-100, var(--crusher-background-surface)));
      display: inline-flex; align-items: center; justify-content: center;
      padding: 0;
      box-shadow: var(--crusher-style-switcher-icon-shadow, var(--outer-shadow, none));
    }
    .style-switcher-toggler { top: 0; animation: crusher-style-switcher-spin 2s linear infinite; }
    .day-night { top: calc(var(--crusher-spacing-10) + var(--crusher-spacing-3) + var(--crusher-spacing-1) - var(--crusher-component-border-weight)); }
    .s-icon:hover {
      box-shadow: var(--crusher-style-switcher-icon-hover-shadow, var(--inner-shadow, var(--crusher-style-switcher-icon-shadow, var(--outer-shadow, none))));
    }
    .icon {
      display: block;
      inline-size: var(--crusher-font-size-xl);
      block-size: var(--crusher-font-size-xl);
    }
    ::slotted([slot="settings-icon"]),
    ::slotted([slot="mode-light-icon"]),
    ::slotted([slot="mode-dark-icon"]) {
      display: block;
      inline-size: var(--crusher-font-size-xl);
      block-size: var(--crusher-font-size-xl);
    }
    @keyframes crusher-style-switcher-spin {
      to { transform: rotate(360deg); }
    }

    h4 {
      margin: 0 0 calc(var(--crusher-spacing-2) + var(--crusher-component-border-weight) * 2);
      color: var(--crusher-style-switcher-heading-color, var(--text-black-700, var(--crusher-text-secondary)));
      font-size: var(--crusher-style-switcher-heading-font-size, var(--crusher-font-size-sm));
      line-height: var(--crusher-style-switcher-heading-line-height, normal);
      font-weight: var(--crusher-font-weight-semibold);
      text-transform: capitalize;
    }
    .colors {
      display: flex;
      flex-wrap: wrap;
      gap: var(--crusher-style-switcher-color-gap, 0);
      justify-content: var(--crusher-style-switcher-color-justify, space-between);
    }
    .swatch {
      appearance: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      height: calc(var(--crusher-spacing-8) - var(--crusher-focus-width));
      width: calc(var(--crusher-spacing-8) - var(--crusher-focus-width));
      border-radius: var(--crusher-radius-full);
      cursor: var(--crusher-style-switcher-cursor, pointer);
      border: 0;
      box-shadow: none;
      padding: 0;
      transition: box-shadow var(--crusher-motion-duration-fast) var(--crusher-motion-easing-standard);
    }
    .s-icon:focus-visible,
    .swatch:focus-visible,
    .dialect-btn:focus-visible {
      outline: var(--crusher-focus-width) solid var(--crusher-color-brand-primary);
      outline-offset: calc(var(--crusher-focus-width) + 1px);
    }
    @media (prefers-reduced-motion: reduce) {
      .style-switcher,
      .s-icon,
      .swatch,
      .style-switcher-toggler {
        transition: none;
        animation: none;
      }
    }
    .swatch.active {
      box-shadow:
        0 0 0 var(--crusher-style-switcher-selected-ring-width, var(--crusher-focus-width))
          var(--crusher-style-switcher-selected-ring, var(--crusher-color-brand-primary)),
        inset 0 0 0 var(--crusher-component-border-weight)
          color-mix(in srgb, var(--crusher-color-base-white) 72%, transparent);
    }
    .panel-extra {
      display: block;
      margin-top: var(--crusher-style-switcher-extra-gap, var(--crusher-spacing-4));
    }
    .panel-extra[hidden] {
      display: none;
    }
    .dialect-row {
      gap: var(--crusher-spacing-2);
      justify-content: flex-start;
    }
    .dialect-btn {
      padding: calc((var(--crusher-spacing-1) + var(--crusher-spacing-2)) / 2) var(--crusher-spacing-2);
      font-size: var(--crusher-font-size-sm);
      border-radius: var(--crusher-radius-md);
      border: var(--crusher-component-border-weight) solid color-mix(in srgb, var(--crusher-border-primary) 88%, transparent);
      background: color-mix(in srgb, var(--crusher-background-surface) 92%, transparent);
      color: var(--crusher-text-primary);
      cursor: var(--crusher-style-switcher-cursor, pointer);
    }
    .dialect-btn.active {
      background: color-mix(in srgb, var(--crusher-color-brand-primary) 16%, transparent);
      border-color: color-mix(in srgb, var(--crusher-color-brand-primary) 44%, var(--crusher-border-primary));
      color: var(--crusher-text-primary);
      box-shadow: 0 0 0 var(--crusher-component-border-weight) color-mix(in srgb, var(--crusher-color-brand-primary) 20%, transparent);
    }
  `;

  static properties = {
    isOpen: { type: Boolean, state: true },
    isDarkMode: { type: Boolean, state: true },
    activeTheme: { type: String, state: true },
    activeBrand: { type: String, state: true },
    hasCustomBrand: { type: Boolean, state: true },
    hasExtra: { type: Boolean, state: true },
    themePrimary: { type: String, state: true },
    /**
     * Color swatches as a JavaScript object map `{ id: hexOrCSSVar }`.
     * Programmatic consumers set this property directly:
     *   el.colors = { sky: '#0ea5e9', emerald: '#10b981' }
     *
     * For HTML-only (no-JS) configuration use the `swatches` attribute instead.
     */
    colors: { type: Object },
    /**
     * HTML-attribute shorthand for configuring color swatches without inline JS.
     * Format: comma-separated `id:#hex` pairs (whitespace around delimiters is ignored).
     *
     * Example:
     *   <crusher-style-switcher
     *     swatches="sky:#0ea5e9, emerald:#10b981, violet:#8b5cf6, orange:#f97316, pink:#ec4899"
     *     hide-themes>
     *   </crusher-style-switcher>
     *
     * Valid hex formats: #rgb, #rrggbb (must start with #).
     * Entries with an invalid hex value are silently skipped.
     * When `swatches` is set it takes precedence over the `colors` property.
     */
    swatches: { type: String, attribute: 'swatches' },
    themes: { type: Array },
    colorTitle: { type: String, attribute: 'color-title' },
    themesTitle: { type: String, attribute: 'themes-title' },
    defaultTheme: { type: String, attribute: 'default-theme' },
    defaultBrand: { type: String, attribute: 'default-brand' },
    hideColors: { type: Boolean, attribute: 'hide-colors' },
    hideMode: { type: Boolean, attribute: 'hide-mode' },
    hideThemeColor: { type: Boolean, attribute: 'hide-theme-color' },
    hideThemes: { type: Boolean, attribute: 'hide-themes' },
    inline: { type: Boolean, reflect: true },
  };

  constructor() {
    super();
    this.isOpen = false;
    this.hideColors = false;
    this.hideMode = false;
    this.hideThemeColor = false;
    this.hideThemes = false;
    // Inline mode renders the switcher as a normal in-flow block instead of
    // the default fixed-position slide-out panel. The gear toggle is hidden
    // and the panel is always visible. Useful for documentation pages,
    // settings panels, and admin UIs that want to embed the switcher.
    this.inline = false;
    this.colorTitle = 'Theme Colors';
    this.themesTitle = 'Dialects';
    this.defaultTheme = '';
    this.defaultBrand = '';
    this.swatches = '';
    this.isDarkMode = document.documentElement.getAttribute('data-mode') === 'dark';
    this.activeTheme = document.documentElement.getAttribute('data-theme') || 'glass';
    this.activeBrand = '';
    this.hasCustomBrand = false;
    this.hasExtra = false;
    this.themePrimary = '';

    this.colors = {
      secondary: '--crusher-color-brand-secondary',
      orange: '--crusher-color-brand-accent-orange',
      pink: '--crusher-color-brand-accent-pink',
      red: '--crusher-color-brand-accent-red',
    };
    this.themes = ['glass', 'brutal', 'neumorph', 'neobrutal', 'minimal', 'futuristic', 'bento', 'professional'];
    this.themePrimaries = {
      glass: '#22c55e',
      minimal: '#111827',
      futuristic: '#22d3ee',
      neobrutal: '#7c3aed',
      neumorph: '#22c55e',
      brutal: '#111111',
      bento: '#16a34a',
      professional: '#2563eb',
    };

    this._syncFromDom = this._syncFromDom.bind(this);
    this._cachedSwatchColors = null;
  }

  /**
   * Parse the `swatches` HTML attribute string into the internal colors-map shape.
   * Format: `id:#hex, id:#hex, ...` (comma-separated; whitespace around `:` and `,` is ignored).
   * Entries with a missing or invalid hex value are skipped (must start with `#`).
   *
   * @param {string} raw — the raw attribute value
   * @returns {{ [id: string]: string }} — object map suitable for `this.colors`
   */
  _parseSwatchesAttr(raw) {
    if (!raw || typeof raw !== 'string') return {};
    const result = {};
    for (const pair of raw.split(',')) {
      const colonIdx = pair.indexOf(':');
      if (colonIdx === -1) continue;
      const id = pair.slice(0, colonIdx).trim();
      const value = pair.slice(colonIdx + 1).trim();
      if (!id || !value.startsWith('#')) continue;
      result[id] = value;
    }
    return result;
  }

  connectedCallback() {
    if (this.defaultTheme && !document.documentElement.hasAttribute('data-theme')) {
      setTheme(this.defaultTheme);
    }
    this.themePrimary = this._readCurrentThemePrimary();
    this._cacheSwatchColors();
    this._restoreBrand();
    super.connectedCallback();
    window.addEventListener(THEME_EVENT, this._syncFromDom);
    this._syncFromDom();
  }

  _cacheSwatchColors() {
    // `swatches` attribute takes precedence over the `colors` property when set.
    const source = this.swatches
      ? this._parseSwatchesAttr(this.swatches)
      : this.colors;
    this._cachedSwatchColors = Array.isArray(source)
      ? source.map((o, i) => this._normalizeColorOption(o, i)).filter(Boolean)
      : Object.entries(source || {}).map(([id, value], i) => this._normalizeColorOption({ id, label: id, value }, i)).filter(Boolean);
  }

  _restoreBrand() {
    const { prefs } = readPrefsState();
    if (prefs && prefs.brand) {
      document.documentElement.style.setProperty('--crusher-color-brand-primary', prefs.brand);
      this.activeBrand = this._normalizeColor(prefs.brand);
      this.hasCustomBrand = true;
      return;
    }
    // Fall back to default-brand attribute when storage is fresh. Sanitization
    // protects against OS-coupled or context-coupled colors that would leak
    // unstable references into the stylesheet.
    if (this.defaultBrand) {
      if (isAllowedDefaultBrand(this.defaultBrand)) {
        const normalized = this._normalizeColor(this.defaultBrand.trim());
        document.documentElement.style.setProperty('--crusher-color-brand-primary', this.defaultBrand.trim());
        this.activeBrand = normalized;
        this.hasCustomBrand = true;
      } else if (!CrusherStyleSwitcher._defaultBrandWarned) {
        CrusherStyleSwitcher._defaultBrandWarned = true;
        console.warn('crusher-style-switcher: default-brand "' + this.defaultBrand + '" rejected by sanitization allowlist; falling back to theme primary');
      }
    }
  }

  disconnectedCallback() {
    window.removeEventListener(THEME_EVENT, this._syncFromDom);
    super.disconnectedCallback();
  }

  _togglePanel() { this.isOpen = !this.isOpen; }

  _toggleDarkMode() {
    setMode(this.isDarkMode ? 'light' : 'dark');
  }

  _normalizeColor(value) {
    if (!value) return '';
    const color = value.trim().toLowerCase();
    if (/^#[0-9a-f]{3}$/.test(color)) {
      return `#${[...color.slice(1)].map((part) => part + part).join('')}`;
    }
    if (/^#[0-9a-f]{6}$/.test(color)) return color;
    const rgb = color.match(/rgba?\(([^)]+)\)/);
    if (!rgb) return color;
    const parts = rgb[1]
      .split(',')
      .slice(0, 3)
      .map((part) => Math.max(0, Math.min(255, Math.round(Number.parseFloat(part.trim())))));
    if (parts.some((part) => Number.isNaN(part))) return color;
    return `#${parts.map((part) => part.toString(16).padStart(2, '0')).join('')}`;
  }

  _readCurrentThemePrimary() {
    return this._normalizeColor(
      getComputedStyle(document.documentElement).getPropertyValue('--crusher-color-brand-primary')
    );
  }

  _getThemePrimary() {
    return this.themePrimary || this.themePrimaries[this.activeTheme] || this.themePrimaries.glass;
  }

  _resolveColorValue(value) {
    const color = String(value || '').trim();
    if (!color) return '';
    if (color.startsWith('--')) {
      return this._normalizeColor(getComputedStyle(document.documentElement).getPropertyValue(color));
    }
    return this._normalizeColor(color);
  }

  _normalizeColorOption(option, index) {
    if (typeof option === 'string') {
      return {
        id: option.startsWith('--') ? option.slice(2) : `color-${index}`,
        label: option.startsWith('--') ? option.slice(2).replace(/-/g, ' ') : option,
        color: this._resolveColorValue(option),
      };
    }

    if (!option || typeof option !== 'object') return null;
    const rawValue = option.value || option.color || option.token;
    const color = this._resolveColorValue(rawValue);
    if (!color) return null;
    const label = option.label || option.id || color;
    if (!option.label && !option.id) {
      // Truthful fallback to hex — surfaces consumer omission without breaking
      // screen-reader output. Warn once per unique color value per session.
      CrusherStyleSwitcher._missingLabelWarned ??= new Set();
      if (!CrusherStyleSwitcher._missingLabelWarned.has(color)) {
        CrusherStyleSwitcher._missingLabelWarned.add(color);
        console.warn('crusher-style-switcher: swatch ' + color + ' is missing { label }; falling back to hex for aria-label');
      }
    }
    return {
      id: option.id || option.label || `color-${index}`,
      label,
      color,
    };
  }

  _normalizeThemeOption(option) {
    if (typeof option === 'string') return { id: option, label: option };
    if (!option || typeof option !== 'object') return null;
    const id = option.id || option.name || option.value;
    if (!id) return null;
    return { id, label: option.label || id };
  }

  _getSwatches() {
    const customColors = this._cachedSwatchColors || [];
    return [
      ...(!this.hideThemeColor ? [{ id: 'theme', label: 'Theme primary', color: this._getThemePrimary() }] : []),
      ...customColors,
    ];
  }

  _getThemes() {
    return (Array.isArray(this.themes) ? this.themes : [])
      .map((option) => this._normalizeThemeOption(option))
      .filter(Boolean);
  }

  _setActiveColor(color) {
    const resolved = this._normalizeColor(color);
    if (!resolved) return;
    document.documentElement.style.setProperty('--crusher-color-brand-primary', resolved);
    this.activeBrand = resolved;
    this.hasCustomBrand = true;
    const { prefs } = readPrefsState();
    // Locked pages persist brand (per-user setting) but NEVER persist theme.
    // Other tabs on the same origin keep their saved theme intact.
    if (!document.documentElement.hasAttribute('data-theme-lock')) {
      prefs.theme = document.documentElement.getAttribute('data-theme')
        || prefs.theme
        || this.activeTheme;
    }
    prefs.brand = resolved;
    writePrefs(prefs);
    window.dispatchEvent(new CustomEvent(BRAND_EVENT, { detail: { brand: resolved } }));
  }

  _resetBrand({ emit = true } = {}) {
    document.documentElement.style.removeProperty('--crusher-color-brand-primary');
    this.activeBrand = '';
    this.hasCustomBrand = false;
    const { prefs } = readPrefsState();
    delete prefs.brand;
    writePrefs(prefs);
    this.themePrimary = this._readCurrentThemePrimary();
    if (emit) {
      window.dispatchEvent(new CustomEvent(BRAND_EVENT, { detail: { brand: this.themePrimary } }));
    }
  }

  _setTheme(name) {
    this._resetBrand({ emit: false });
    setTheme(name);
    this.themePrimary = this._readCurrentThemePrimary();
    window.dispatchEvent(new CustomEvent(BRAND_EVENT, { detail: { brand: this.themePrimary } }));
  }

  _selectSwatch(swatch) {
    if (swatch.id === 'theme') {
      this._resetBrand();
      return;
    }
    this._setActiveColor(swatch.color);
  }

  _isActiveSwatch(swatch) {
    if (!this.hasCustomBrand) {
      return swatch.id === 'theme' || (this.hideThemeColor && swatch.color === this._getThemePrimary());
    }
    return swatch.id !== 'theme' && this.activeBrand === swatch.color;
  }

  _syncFromDom() {
    this.isDarkMode = document.documentElement.getAttribute('data-mode') === 'dark';
    this.activeTheme = document.documentElement.getAttribute('data-theme') || 'glass';
    this.activeBrand = this.hasCustomBrand
      ? this._normalizeColor(getComputedStyle(document.documentElement).getPropertyValue('--crusher-color-brand-primary'))
      : '';
    this._cacheSwatchColors();
  }

  _handleExtraSlotChange(event) {
    this.hasExtra = event.target.assignedElements({ flatten: true }).length > 0;
  }

  _iconTemplate(kind) {
    if (kind === 'gear') {
      return html`
        <svg class="icon gear-icon" viewBox="0 0 512 512" fill="currentColor" aria-hidden="true">
          <path d="M495.9 166.6c3.2 8.7.5 18.4-6.4 24.6l-43.3 39.4c1.1 8.3 1.7 16.8 1.7 25.4s-.6 17.1-1.7 25.4l43.3 39.4c6.9 6.2 9.6 15.9 6.4 24.6-4.4 12.1-9.8 23.6-16.1 34.5l-3.7 6.4c-6.5 10.9-14 21.1-22.3 30.7-6.1 7-15.8 9.3-24.5 6.4l-55.7-17.7c-13.4 10.3-28.2 18.9-44 25.4l-12.5 57.1c-2 9.1-9 16.3-18.2 17.8-14.1 2.3-28.4 3.5-43 3.5s-28.9-1.2-43-3.5c-9.2-1.5-16.2-8.7-18.2-17.8L182.3 431c-15.8-6.5-30.6-15.1-44-25.4l-55.7 17.7c-8.8 2.9-18.5.6-24.5-6.4-8.3-9.6-15.8-19.8-22.3-30.7l-3.7-6.4c-6.3-10.9-11.7-22.4-16.1-34.5-3.2-8.7-.5-18.4 6.4-24.6l43.3-39.4C64.6 273.1 64 264.6 64 256s.6-17.1 1.7-25.4l-43.3-39.4c-6.9-6.2-9.6-15.9-6.4-24.6 4.4-12.1 9.8-23.6 16.1-34.5l3.7-6.4c6.5-10.9 14-21.1 22.3-30.7 6.1-7 15.8-9.3 24.5-6.4l55.7 17.7c13.4-10.3 28.2-18.9 44-25.4l12.5-57.1c2-9.1 9-16.3 18.2-17.8 14.1-2.3 28.4-3.5 43-3.5s28.9 1.2 43 3.5c9.2 1.5 16.2 8.7 18.2 17.8L329.7 81c15.8 6.5 30.6 15.1 44 25.4l55.7-17.7c8.8-2.9 18.5-.6 24.5 6.4 8.3 9.6 15.8 19.8 22.3 30.7l3.7 6.4c6.3 10.9 11.7 22.4 16.1 34.5zM256 336a80 80 0 1 0 0-160 80 80 0 1 0 0 160z"></path>
        </svg>
      `;
    }
    if (kind === 'sun') {
      return html`
        <svg class="icon" viewBox="0 0 512 512" fill="currentColor" aria-hidden="true">
          <path d="M361.5 1.2c5 2.1 8.6 6.6 9.6 11.9L391 121l107.9 19.8c5.3 1 9.8 4.6 11.9 9.6s1.5 10.7-1.6 15.2L446.9 256l62.3 90.3c3.1 4.5 3.7 10.2 1.6 15.2s-6.6 8.6-11.9 9.6L391 391 371.1 498.9c-1 5.3-4.6 9.8-9.6 11.9s-10.7 1.5-15.2-1.6L256 446.9l-90.3 62.3c-4.5 3.1-10.2 3.7-15.2 1.6s-8.6-6.6-9.6-11.9L121 391 13.1 371.1c-5.3-1-9.8-4.6-11.9-9.6s-1.5-10.7 1.6-15.2L65.1 256 2.8 165.7c-3.1-4.5-3.7-10.2-1.6-15.2s6.6-8.6 11.9-9.6L121 121 140.9 13.1c1-5.3 4.6-9.8 9.6-11.9s10.7-1.5 15.2 1.6L256 65.1 346.3 2.8c4.5-3.1 10.2-3.7 15.2-1.6zM160 256a96 96 0 1 1 192 0 96 96 0 1 1 -192 0zm224 0a128 128 0 1 0 -256 0 128 128 0 1 0 256 0z"></path>
        </svg>
      `;
    }
    return html`
      <svg class="icon" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
        <path d="M223.5 32C100 32 0 132.3 0 256S100 480 223.5 480c60.6 0 115.5-24.2 155.8-63.4c5-4.9 6.3-12.5 3.1-18.7s-10.1-9.7-17-8.5c-9.8 1.7-19.8 2.6-30.1 2.6c-96.9 0-175.5-78.8-175.5-176c0-65.8 36-123.1 89.3-153.3c6.1-3.5 9.2-10.5 7.7-17.3s-7.3-11.9-14.3-12.5c-6.3-.5-12.6-.8-19-.8z"></path>
      </svg>
    `;
  }

  _renderIconSlot(name, fallbackKind) {
    return html`<slot name=${name}>${this._iconTemplate(fallbackKind)}</slot>`;
  }

  render() {
    const classes = { 'style-switcher': true, open: this.isOpen };
    const swatches = this._getSwatches();
    const themes = this._getThemes();
    return html`
      <div class=${classMap(classes)}>
        <button
          type="button"
          class="style-switcher-toggler s-icon"
          @click=${this._togglePanel}
          title="Open styles"
          aria-label="Open style settings"
          aria-expanded=${this.isOpen ? 'true' : 'false'}
        >
          ${this._renderIconSlot('settings-icon', 'gear')}
        </button>
        ${!this.hideMode ? html`
          <button
            type="button"
            class="day-night s-icon"
            @click=${this._toggleDarkMode}
            title="Toggle dark mode"
            aria-label=${this.isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-pressed=${this.isDarkMode ? 'true' : 'false'}
          >
            ${this.isDarkMode
              ? this._renderIconSlot('mode-light-icon', 'sun')
              : this._renderIconSlot('mode-dark-icon', 'moon')}
          </button>
        ` : ''}

        ${!this.hideColors && swatches.length ? html`
          <h4><slot name="colors-title">${this.colorTitle}</slot></h4>
          <div class="colors">
            ${swatches.map((swatch) => {
              const active = this._isActiveSwatch(swatch);
              return html`
                <button
                  type="button"
                  class="swatch ${active ? 'active' : ''}"
                  style="background: ${swatch.color}"
                  title=${swatch.label}
                  aria-label=${swatch.label}
                  aria-pressed=${active ? 'true' : 'false'}
                  @click=${() => this._selectSwatch(swatch)}
                ></button>
              `;
            })}
          </div>
        ` : ''}

        ${!this.hideThemes && themes.length ? html`
          <h4 style="margin-top: var(--crusher-spacing-4)"><slot name="themes-title">${this.themesTitle}</slot></h4>
          <div class="colors dialect-row">
            ${themes.map((theme) => {
              const active = this.activeTheme === theme.id;
              return html`
                <button
                  type="button"
                  class="btn dialect-btn ${active ? 'active' : ''}"
                  aria-pressed=${active ? 'true' : 'false'}
                  @click=${() => this._setTheme(theme.id)}
                >${theme.label}</button>
              `;
            })}
          </div>
        ` : ''}

        <slot class="panel-extra" name="extra" ?hidden=${!this.hasExtra} @slotchange=${this._handleExtraSlotChange}></slot>
      </div>
    `;
  }
}

customElements.define('crusher-style-switcher', CrusherStyleSwitcher);
